import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Search, Plus, ArrowUpRight, Check, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useChainStore } from "../../data/chainStore";
import { api, apiError } from "../../lib/api";

/* P3 · Contact Profile · Transactions Management (2026-02-28)
 * ────────────────────────────────────────────────────────────
 * Lightweight transaction manager surfaced from the Contact
 * Profile's Quick Actions row.  Two panes:
 *
 *   Pane A · "Current Transactions"
 *     Every Deal Room the contact currently belongs to, with
 *     their role in that deal + a click-through to the
 *     canonical Deal Room route.  Reads from the SSOT
 *     (`dealsForContact` on the chainStore) — no fetch, no new
 *     endpoint.
 *
 *   Pane B · "+ Add to Transaction"
 *     Searchable list of every Deal Room in the workspace.
 *     Each row exposes a role selector + an "Add" button that
 *     fires the canonical
 *       POST /api/v2/deals/{deal_id}/participants
 *       body: { participant_type, full_name, email, phone, contact_id }
 *     endpoint used by every other participant-creation
 *     surface (CreateDealModal, DealRoomCanonicalPage, the
 *     participants modal).  Duplicate assignments are detected
 *     up-front from the SSOT so the row reads "Already a
 *     participant" instead of firing a redundant POST.
 *
 * No new endpoint · no new permission · no duplicate business
 * logic.  Existing role vocabulary + validation live on the
 * backend already; we simply reuse them.
 */

/* Canonical participant type vocabulary — mirrors the
 * enumeration validated on the backend at
 * `routes_persistence.py::add_participant` (`participant_type`
 * whitelist).  Renamed to human-friendly labels here so the
 * dropdown reads naturally. */
const ROLE_OPTIONS = [
  { value: "buyer",            label: "Buyer" },
  { value: "seller",           label: "Seller" },
  { value: "buyer_solicitor",  label: "Buyer solicitor" },
  { value: "seller_solicitor", label: "Seller solicitor" },
  { value: "mortgage_broker",  label: "Mortgage broker" },
  { value: "negotiator",       label: "Negotiator" },
  { value: "progressor",       label: "Sales progressor" },
  { value: "other",            label: "Other" },
];

const humanRole = (type) => ROLE_OPTIONS.find((r) => r.value === type)?.label
  || String(type || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const ContactTransactionsModal = ({ open, onClose, contact }) => {
  const { deals, findProperty, dealsForContact } = useChainStore();
  const nav = useNavigate();

  const [mode, setMode] = useState("current");     // "current" | "add"
  const [query, setQuery] = useState("");
  const [pendingRole, setPendingRole] = useState({}); // dealId → role value
  const [busyDealId, setBusyDealId] = useState(null);

  // Contact's existing deal set + role map — pulled from SSOT.
  // Rebuilt on every open so the pane always reads live data.
  const currentDeals = useMemo(
    () => (contact ? dealsForContact(contact.id) : []),
    [contact, dealsForContact],
  );

  /* Build a set of dealIds the contact is already linked to so
   * the "+ Add to Transaction" pane can render "Already a
   * participant" in-place instead of firing a redundant POST. */
  const currentDealIds = useMemo(() => {
    const s = new Set();
    for (const d of currentDeals) s.add(d.id);
    return s;
  }, [currentDeals]);

  /* Every-workspace deal list filtered by the search query.
   * Search matches address, deal id, or the deal.name field so
   * operators can find a room the same way they do everywhere
   * else. */
  const filteredDeals = useMemo(() => {
    const t = query.trim().toLowerCase();
    const list = Array.isArray(deals) ? deals : [];
    return list.map((d) => {
      const prop = findProperty(d.propertyId);
      const address = prop?.address || d.name || d.id;
      const primary = String(address || "").split(",")[0].trim();
      return { deal: d, address, primary, prop };
    }).filter(({ address, deal }) => {
      if (!t) return true;
      const hay = `${address} ${deal.id} ${deal.name || ""}`.toLowerCase();
      return hay.includes(t);
    }).slice(0, 60);
  }, [deals, findProperty, query]);

  useEffect(() => {
    if (!open) {
      setMode("current");
      setQuery("");
      setPendingRole({});
      setBusyDealId(null);
    }
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !contact) return null;

  const handleAdd = async (deal, roleValue) => {
    if (!contact || !deal) return;
    if (currentDealIds.has(deal.id)) return;
    setBusyDealId(deal.id);
    try {
      await api.post(`/api/v2/deals/${deal.id}/participants`, {
        participant_type: roleValue,
        full_name:        contact.name || "",
        email:            contact.email || null,
        phone:            contact.phone || null,
        contact_id:       contact.id,
      });
      toast.success(`${contact.name} added to ${(findProperty(deal.propertyId)?.address || deal.name || "deal").split(",")[0]}`);
      // Refresh chainStore so `dealsForContact` reflects the new link.
      // The Deal Rooms list, Command Centre and Contact Profile all
      // read from the same SSOT so a store refresh is enough.
      window.dispatchEvent(new CustomEvent("cf:deals:refresh"));
      // Return to the Current Transactions pane so the operator
      // sees the freshly-added transaction at the top.
      setMode("current");
    } catch (e) {
      toast.error(apiError(e) || "Could not add to transaction");
    } finally {
      setBusyDealId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center px-4 pt-[10vh]"
      data-testid="contact-transactions-modal"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${contact.name} · Transactions`}
        className="relative w-full max-w-[640px] rounded-2xl bg-white border border-slate-200 shadow-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-slate-100">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400">
              Transactions
            </p>
            <h2 className="text-[15px] font-bold text-slate-900 truncate leading-tight">
              {contact.name}
            </h2>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setMode("current")}
              className={`h-8 px-3 rounded-lg text-[12px] font-semibold ring-1 transition-colors ${
                mode === "current"
                  ? "bg-slate-900 text-white ring-slate-900"
                  : "bg-white text-slate-600 ring-slate-200 hover:text-slate-900"
              }`}
              data-testid="contact-transactions-tab-current"
            >
              Current · {currentDeals.length}
            </button>
            <button
              type="button"
              onClick={() => setMode("add")}
              className={`inline-flex items-center gap-1 h-8 px-3 rounded-lg text-[12px] font-semibold ring-1 transition-colors ${
                mode === "add"
                  ? "bg-indigo-600 text-white ring-indigo-600"
                  : "bg-white text-indigo-700 ring-indigo-200 hover:bg-indigo-50"
              }`}
              data-testid="contact-transactions-tab-add"
            >
              <Plus size={12} strokeWidth={2.4} /> Add to Transaction
            </button>
            <button
              type="button"
              onClick={onClose}
              className="ml-1 inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              aria-label="Close transactions manager"
              data-testid="contact-transactions-close"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="max-h-[62vh] overflow-y-auto">
          {/* Pane A — Current Transactions */}
          {mode === "current" && (
            <div className="p-3" data-testid="contact-transactions-pane-current">
              {currentDeals.length === 0 ? (
                <div className="px-3 py-8 text-center">
                  <p className="text-[12.5px] text-slate-500">
                    {contact.name} is not currently linked to any Deal Room.
                  </p>
                  <button
                    type="button"
                    onClick={() => setMode("add")}
                    className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                    data-testid="contact-transactions-empty-add"
                  >
                    <Plus size={12} strokeWidth={2.4} /> Add to Transaction
                  </button>
                </div>
              ) : (
                <ul className="space-y-1">
                  {currentDeals.map((d) => {
                    const prop = findProperty(d.propertyId);
                    const primary = String(prop?.address || d.name || d.id).split(",")[0].trim();
                    const roleType = d._participantRoleForContact
                      || d.role_for_contact
                      || d.participant_type_for_contact
                      || null;
                    const roleLabel = roleType ? humanRole(roleType)
                      : d._participantRoleLabel || "Participant";
                    return (
                      <li key={d.id}>
                        <button
                          type="button"
                          onClick={() => { onClose?.(); nav(`/deal-rooms/${d.id}`); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left"
                          data-testid={`contact-transactions-current-row-${d.id}`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-slate-50 via-white to-slate-100 ring-1 ring-slate-200/60 flex items-center justify-center shrink-0">
                            <Building2 size={14} className="text-slate-500" strokeWidth={1.75} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-bold text-slate-900 truncate">
                              {primary}
                            </p>
                            <p className="text-[11px] text-slate-500 truncate">
                              Role: <span className="font-semibold text-slate-700">{roleLabel}</span>
                            </p>
                          </div>
                          <ArrowUpRight size={13} className="text-slate-300" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Pane B — Add to Transaction */}
          {mode === "add" && (
            <div className="p-3" data-testid="contact-transactions-pane-add">
              {/* Search */}
              <div className="relative mb-2">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                  placeholder="Search Deal Rooms by address, name or id…"
                  className="w-full h-9 pl-9 pr-3 rounded-lg text-[12.5px] ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  data-testid="contact-transactions-search"
                />
              </div>
              {filteredDeals.length === 0 ? (
                <p className="px-3 py-6 text-center text-[12px] text-slate-500">
                  No Deal Rooms match &ldquo;{query}&rdquo;.
                </p>
              ) : (
                <ul className="space-y-1">
                  {filteredDeals.map(({ deal, primary }) => {
                    const already = currentDealIds.has(deal.id);
                    const roleValue = pendingRole[deal.id] || "buyer_solicitor";
                    const isBusy = busyDealId === deal.id;
                    return (
                      <li
                        key={deal.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                        data-testid={`contact-transactions-add-row-${deal.id}`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-slate-50 via-white to-slate-100 ring-1 ring-slate-200/60 flex items-center justify-center shrink-0">
                          <Building2 size={13} className="text-slate-500" strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[12.5px] font-bold text-slate-900 truncate">
                            {primary}
                          </p>
                          <p className="text-[10.5px] text-slate-500 truncate">
                            {deal.stage ? String(deal.stage).replace(/_/g, " ") : "Deal Room"}
                          </p>
                        </div>
                        {already ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 h-7 rounded-md bg-emerald-50 ring-1 ring-emerald-200 text-[11px] font-semibold text-emerald-700"
                            data-testid={`contact-transactions-already-${deal.id}`}
                          >
                            <Check size={11} strokeWidth={2.6} /> Already a participant
                          </span>
                        ) : (
                          <>
                            <select
                              value={roleValue}
                              onChange={(e) => setPendingRole((prev) => ({ ...prev, [deal.id]: e.target.value }))}
                              className="h-7 px-2 rounded-md text-[11.5px] font-medium ring-1 ring-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                              data-testid={`contact-transactions-role-${deal.id}`}
                              aria-label={`Role for ${primary}`}
                            >
                              {ROLE_OPTIONS.map((r) => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => handleAdd(deal, roleValue)}
                              disabled={isBusy}
                              className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11.5px] font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors ${
                                isBusy ? "opacity-60 cursor-not-allowed" : ""
                              }`}
                              data-testid={`contact-transactions-add-btn-${deal.id}`}
                            >
                              {isBusy ? "Adding…" : "Add"}
                            </button>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactTransactionsModal;
